// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Modal, Platform, Pressable, StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import appColors, { alpha } from '@/theme/colors';

type TargetLayout = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type CoachMarkCalloutProps = {
  visible: boolean;
  targetRef: RefObject<View | null>;
  title: string;
  description: string;
  actionLabel: string;
  /** Hides the tip for now; it may come back on a later visit. */
  onDismiss: () => void;
  /** Retires this one tip for good. Omit to hide the option. */
  onDismissForever?: () => void;
  /** Turns off every coach tip for this user. Omit to hide the option. */
  onDisableAll?: () => void;
  hideForeverLabel?: string;
  disableAllLabel?: string;
};

const CALLOUT_WIDTH = 280;
const EDGE_PADDING = 12;
const TARGET_GAP = 10;
const DISMISS_GUARD_MS = 350;
const MEASURE_INTERVAL_MS = 200;
const ARROW_SIZE = 10;

export const CoachMarkCallout = ({
  visible,
  targetRef,
  title,
  description,
  actionLabel,
  onDismiss,
  onDismissForever,
  onDisableAll,
  hideForeverLabel,
  disableAllLabel,
}: CoachMarkCalloutProps) => {
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const [target, setTarget] = useState<TargetLayout | null>(null);
  const [calloutHeight, setCalloutHeight] = useState(140);
  const [dismissEnabled, setDismissEnabled] = useState(false);
  const dismissEnabledRef = useRef(false);
  const onDismissRef = useRef(onDismiss);

  useEffect(() => {
    dismissEnabledRef.current = dismissEnabled;
  }, [dismissEnabled]);

  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!visible) {
      setDismissEnabled(false);
      return;
    }

    const timer = setTimeout(() => setDismissEnabled(true), DISMISS_GUARD_MS);
    return () => clearTimeout(timer);
  }, [visible]);

  // On web the modal backdrop swallows wheel events, so a mouse user can't scroll the page
  // while a tip is up. Treat scrolling like clicking away: dismiss and let them get on with it.
  // The handler reads through refs so the listener stays stable while the tip is showing; the
  // dismiss guard applies just as it does for backdrop presses.
  useEffect(() => {
    if (!visible) return;
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.addEventListener) return;

    const handleWheel = () => {
      if (dismissEnabledRef.current) onDismissRef.current();
    };
    window.addEventListener('wheel', handleWheel, { passive: true });
    // Optional call: React Native's jest preset defines `window` without the EventTarget API,
    // so cleanup can run against a window whose listener functions have been torn down.
    return () => window.removeEventListener?.('wheel', handleWheel);
  }, [visible]);

  useEffect(() => {
    if (!visible || !targetRef.current) return;

    const measure = () => {
      targetRef.current?.measureInWindow((x, y, width, height) => {
        setTarget({ x, y, width, height });
      });
    };

    measure();
    const timer = setTimeout(measure, 50);
    const interval = setInterval(measure, MEASURE_INTERVAL_MS);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [targetRef, visible, windowHeight, windowWidth]);

  const layout = useMemo(() => {
    if (!target) {
      return null;
    }

    const desiredLeft = target.x + target.width / 2 - CALLOUT_WIDTH / 2;
    const left = Math.min(
      Math.max(EDGE_PADDING, desiredLeft),
      Math.max(EDGE_PADDING, windowWidth - CALLOUT_WIDTH - EDGE_PADDING)
    );

    const showAbove = target.y + target.height + TARGET_GAP + calloutHeight > windowHeight - EDGE_PADDING;
    const top = showAbove
      ? Math.max(EDGE_PADDING, target.y - calloutHeight - TARGET_GAP)
      : Math.min(windowHeight - calloutHeight - EDGE_PADDING, target.y + target.height + TARGET_GAP);

    const targetCenterX = target.x + target.width / 2;
    const arrowLeft = Math.min(
      CALLOUT_WIDTH - EDGE_PADDING - ARROW_SIZE * 2,
      Math.max(EDGE_PADDING, targetCenterX - left - ARROW_SIZE)
    );

    return {
      left,
      top,
      arrowLeft,
      showAbove,
    };
  }, [calloutHeight, target, windowHeight, windowWidth]);

  const isTargetVisible = useMemo(() => {
    if (!target) return false;
    if (target.width <= 0 || target.height <= 0) return false;

    const targetRight = target.x + target.width;
    const targetBottom = target.y + target.height;

    const horizontallyVisible = targetRight > 0 && target.x < windowWidth;
    const verticallyVisible = targetBottom > 0 && target.y < windowHeight;

    return horizontallyVisible && verticallyVisible;
  }, [target, windowHeight, windowWidth]);

  if (!visible || !isTargetVisible || !layout) {
    return null;
  }

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onDismiss}>
      <Pressable style={styles.overlay} onPress={dismissEnabled ? onDismiss : undefined}>
        <Pressable
          style={[
            styles.card,
            {
              left: layout.left,
              top: layout.top,
            },
          ]}
          onLayout={(event) => setCalloutHeight(event.nativeEvent.layout.height)}
          onPress={() => null}
        >
          <View
            style={[
              styles.arrow,
              layout.showAbove ? styles.arrowBottom : styles.arrowTop,
              { left: layout.arrowLeft },
            ]}
          />
          <View style={styles.headerRow}>
            <Text style={styles.title}>{title}</Text>
            <Pressable onPress={onDismiss} style={styles.closeButton}>
              <Text style={styles.closeText}>×</Text>
            </Pressable>
          </View>
          <Text style={styles.description}>{description}</Text>
          <Pressable style={styles.actionButton} onPress={onDismiss}>
            <Text style={styles.actionText}>{actionLabel}</Text>
          </Pressable>
          {onDismissForever || onDisableAll ? (
            <View style={styles.optOutRow}>
              {onDismissForever && hideForeverLabel ? (
                <Pressable
                  testID="coach-mark-hide-forever"
                  style={styles.optOutButton}
                  onPress={onDismissForever}
                  accessibilityRole="button"
                >
                  <Text style={styles.optOutText}>{hideForeverLabel}</Text>
                </Pressable>
              ) : null}
              {onDisableAll && disableAllLabel ? (
                <Pressable
                  testID="coach-mark-disable-all"
                  style={styles.optOutButton}
                  onPress={onDisableAll}
                  accessibilityRole="button"
                >
                  <Text style={styles.optOutText}>{disableAllLabel}</Text>
                </Pressable>
              ) : null}
            </View>
          ) : null}
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: alpha(appColors.neutral.navy, 0.45),
  },
  card: {
    position: 'absolute',
    width: CALLOUT_WIDTH,
    borderRadius: 8,
    backgroundColor: appColors.surface.light,
    borderWidth: 1,
    borderColor: appColors.border.onLight,
    padding: 16,
    shadowColor: appColors.text.secondary,
    shadowOpacity: 0.2,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 8 },
    elevation: 8,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  closeButton: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeText: {
    color: appColors.surface.tintDeep,
    fontSize: 24,
    lineHeight: 24,
    fontWeight: '300',
  },
  title: {
    color: appColors.surface.brownDeep,
    fontSize: 16,
    fontWeight: '700',
    flexShrink: 1,
  },
  description: {
    color: appColors.neutral.charcoal,
    fontSize: 14,
    marginTop: 10,
    lineHeight: 22,
  },
  actionButton: {
    alignSelf: 'flex-end',
    marginTop: 16,
    borderRadius: 8,
    backgroundColor: appColors.background.interactive,
    paddingVertical: 10,
    paddingHorizontal: 18,
  },
  actionText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 15,
  },
  optOutRow: {
    marginTop: 12,
    borderTopWidth: 1,
    borderTopColor: appColors.border.onLight,
    paddingTop: 10,
    gap: 4,
  },
  optOutButton: {
    paddingVertical: 4,
  },
  optOutText: {
    color: appColors.text.onLight,
    fontSize: 13,
    textDecorationLine: 'underline',
  },
  arrow: {
    position: 'absolute',
    width: ARROW_SIZE * 2,
    height: ARROW_SIZE * 2,
    backgroundColor: appColors.surface.light,
    borderLeftWidth: 1,
    borderTopWidth: 1,
    borderColor: appColors.border.onLight,
    transform: [{ rotate: '45deg' }],
  },
  arrowTop: {
    top: -ARROW_SIZE,
  },
  arrowBottom: {
    bottom: -ARROW_SIZE,
    transform: [{ rotate: '225deg' }],
  },
});
